/* gr-cart-page.js — Cart page interactivity.
 *   - Qty +/- with debounce POST to /cart/change.js
 *   - Remove line (qty → 0)
 *   - Order note POST to /cart/update.js
 *   - Discount field redirects to checkout with ?discount=CODE
 *   - Updates subtotal, total, ship bar, counts on mutation without reload
 *   - Broadcasts 'gr:cart-updated' for header count + drawer
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-gr-cart-page]');
  if (!root) return;

  var linesEl    = root.querySelector('[data-gr-cart-lines]');
  var subtotalEl = root.querySelector('[data-gr-cart-subtotal]');
  var totalEl    = root.querySelector('[data-gr-cart-total]');
  var shipEl     = root.querySelector('[data-gr-ship]');
  var shipFillEl = root.querySelector('.gr-cart-page__ship-fill');
  var shipTextEl = shipEl ? shipEl.querySelector('p') : null;
  var countEl    = root.querySelector('.gr-cart-page__count');
  var noteEl     = root.querySelector('[data-gr-cart-note]');
  var noteMirror = root.querySelector('[data-gr-cart-note-mirror]');
  var discInput  = root.querySelector('[data-gr-discount-input]');
  var discApply  = root.querySelector('[data-gr-discount-apply]');
  var discNote   = root.querySelector('[data-gr-discount-note]');
  var threshold  = parseInt(root.dataset.shipThreshold, 10) || 7500;

  // ── Formatting ────────────────────────────────────────
  function money(cents) {
    var n = (parseInt(cents, 10) || 0) / 100;
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(n); }
    catch (e) { return '$' + n.toFixed(2); }
  }

  function broadcast(cart) {
    document.dispatchEvent(new CustomEvent('gr:cart-updated', { detail: cart }));
    document.querySelectorAll('[data-gr-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
    });
  }

  function updateTotals(cart) {
    if (subtotalEl) subtotalEl.textContent = money(cart.items_subtotal_price);
    if (totalEl)    totalEl.textContent    = money(cart.total_price) + ' ' + (cart.currency || '');
    if (countEl)    countEl.textContent    = '(' + cart.item_count + ')';

    // Heading: "<count> ITEM" / "<count> ITEMS" — pluralized via data-i18n
    var headingCount = root.querySelector('[data-gr-cart-page-count]');
    var headingLabel = root.querySelector('[data-gr-cart-page-items-label]');
    if (headingCount) headingCount.textContent = cart.item_count;
    if (headingLabel) {
      headingLabel.textContent = (cart.item_count === 1)
        ? (root.dataset.i18nItemsOne || 'ITEM')
        : (root.dataset.i18nItemsOther || 'ITEMS');
    }

    if (shipEl && shipFillEl && shipTextEl) {
      var have = cart.total_price;
      var remaining = threshold - have;
      while (shipTextEl.firstChild) shipTextEl.removeChild(shipTextEl.firstChild);
      if (remaining > 0) {
        var pct = Math.min(100, Math.round((have / threshold) * 100));
        shipFillEl.style.width = pct + '%';
        var addTpl = root.dataset.i18nFreeShipAdd || 'Add {amount} for FREE SHIPPING';
        var parts = addTpl.split('{amount}');
        shipTextEl.appendChild(document.createTextNode(parts[0] || ''));
        var amtSpan = document.createElement('span');
        amtSpan.className = 'gr-cart-page__ship-amount';
        amtSpan.textContent = money(remaining);
        shipTextEl.appendChild(amtSpan);
        if (parts[1]) shipTextEl.appendChild(document.createTextNode(parts[1]));
      } else {
        shipFillEl.style.width = '100%';
        shipTextEl.classList.add('gr-cart-page__ship-unlocked');
        shipTextEl.textContent = root.dataset.i18nFreeShipUnlocked || '✦ You unlocked FREE SHIPPING. Ride on.';
      }
    }
  }

  function reloadIfEmpty(cart) {
    if (cart.item_count === 0) {
      // Reload to show the empty state section (Liquid-rendered)
      window.location.reload();
    }
  }

  // ── /cart/change.js by key ────────────────────────────
  function changeLine(key, quantity, li) {
    if (li) li.setAttribute('data-updating', '');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (quantity === 0 && li) {
          li.style.opacity = '0';
          li.style.maxHeight = li.offsetHeight + 'px';
          setTimeout(function () {
            li.style.transition = 'max-height .24s ease, opacity .2s, padding .24s';
            li.style.maxHeight = '0';
            li.style.padding = '0';
            setTimeout(function () { li.remove(); }, 260);
          }, 20);
        } else if (li) {
          li.removeAttribute('data-updating');
          // Update line price if returned
          var updated = (cart.items || []).find(function (i) { return i.key === key; });
          if (updated) {
            var priceEl = li.querySelector('.gr-cart-line__price');
            if (priceEl) priceEl.textContent = money(updated.final_line_price);
          }
        }
        updateTotals(cart);
        broadcast(cart);
        reloadIfEmpty(cart);
      })
      .catch(function () { if (li) li.removeAttribute('data-updating'); });
  }

  // ── Qty handlers ──────────────────────────────────────
  var qtyTimers = {};

  function handleQty(li) {
    var input = li.querySelector('[data-gr-qty-input]');
    var dec   = li.querySelector('[data-gr-qty-dec]');
    var inc   = li.querySelector('[data-gr-qty-inc]');
    var key   = input.dataset.key;

    function commit(val) {
      val = Math.max(0, parseInt(val, 10) || 0);
      input.value = val;
      clearTimeout(qtyTimers[key]);
      qtyTimers[key] = setTimeout(function () { changeLine(key, val, li); }, 260);
    }

    dec.addEventListener('click', function () { commit(parseInt(input.value, 10) - 1); });
    inc.addEventListener('click', function () { commit(parseInt(input.value, 10) + 1); });
    input.addEventListener('change', function () { commit(input.value); });
    input.addEventListener('input', function () {
      if (input.value.length > 0 && !isNaN(+input.value)) commit(input.value);
    });
  }

  linesEl && linesEl.querySelectorAll('[data-gr-cart-line]').forEach(handleQty);

  // ── Remove ────────────────────────────────────────────
  if (linesEl) {
    linesEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-gr-cart-remove]');
      if (!btn) return;
      var li = btn.closest('[data-gr-cart-line]');
      changeLine(btn.dataset.key, 0, li);
    });
  }

  // ── Note (debounced) ──────────────────────────────────
  if (noteEl) {
    var noteTimer;
    noteEl.addEventListener('input', function () {
      clearTimeout(noteTimer);
      noteTimer = setTimeout(function () {
        var note = noteEl.value;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: note })
        }).then(function (r) { return r.json(); }).then(function (cart) {
          if (noteMirror) noteMirror.value = note;
          broadcast(cart);
        });
      }, 500);
    });
  }

  // ── Discount apply ────────────────────────────────────
  // Mirror the cart drawer's flow: POST to /discount/CODE so Shopify
  // sets the discount cookie, verify via /cart.js, then reload the
  // page so the Liquid-rendered discount list / new totals appear.
  // Falls back to the previous "redirect to checkout" UX only on
  // network error so the customer can still complete the purchase.
  if (discApply && discInput) {
    var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var origNote = discNote ? discNote.textContent : '';
    var origLabel = discApply.textContent;
    var INVALID_CODE_MSG = root.dataset.i18nInvalidCode || 'Invalid code. Try again.';

    var applyDiscount = function () {
      var code = (discInput.value || '').trim();
      if (!code) return;

      discApply.disabled = true;
      discApply.textContent = '…';
      if (discNote) discNote.textContent = '';

      fetch(ROOT + 'discount/' + encodeURIComponent(code), { method: 'GET', redirect: 'follow' })
        .then(function () {
          // Give Shopify a moment to commit the cookie
          return new Promise(function (resolve) { setTimeout(resolve, 800); });
        })
        .then(function () {
          return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          var applied = (cart.cart_level_discount_applications || []).some(function (d) {
            return (d.title || '').toUpperCase() === code.toUpperCase();
          });
          if (applied || cart.total_discount > 0) {
            // Reload so Liquid re-renders the cart-level discount list
            // and totals with the applied discount in place.
            window.location.reload();
          } else {
            // Invalid code — clear the cookie and show error
            document.cookie = 'discount_code=; path=/; max-age=0';
            if (discNote) discNote.textContent = INVALID_CODE_MSG;
            discApply.disabled = false;
            discApply.textContent = origLabel;
          }
        })
        .catch(function () {
          // Network error — fall back to passing the code via the
          // checkout URL so the customer isn't blocked from finishing.
          window.location.href = ROOT + 'checkout?discount=' + encodeURIComponent(code);
        });
    };

    discApply.addEventListener('click', applyDiscount);
    discInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); }
    });
  }

  // ── Discount remove ───────────────────────────────────
  // Clear the discount cookie + GET /checkout?discount= to wipe the
  // server-side cookie too, then reload so Liquid re-renders without
  // the applied discount.
  root.querySelectorAll('[data-gr-discount-remove]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      btn.disabled = true;
      var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      document.cookie = 'discount_code=; path=/; max-age=0';
      fetch(ROOT + 'checkout?discount=', { method: 'GET', redirect: 'manual' })
        .catch(function () {})
        .then(function () { window.location.reload(); });
    });
  });
})();
